import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  PlayIcon,
  PauseIcon,
  ArrowPathIcon,
  ChartBarIcon,
  UserGroupIcon,
} from '@heroicons/react/24/solid';

interface QuestionData {
  id: string;
  text: string;
  initialDifficulty: number;
  currentDifficulty: number;
  totalAttempts: number;
  correctAttempts: number;
  correctRate: number;
}

const DIFFICULTY_BANDS = [
  { min: 0.90, max: 1.01, label: 'Very Easy', level: 1, color: '#10b981' },
  { min: 0.75, max: 0.90, label: 'Easy', level: 2, color: '#22c55e' },
  { min: 0.55, max: 0.75, label: 'Medium', level: 3, color: '#f59e0b' },
  { min: 0.35, max: 0.55, label: 'Hard', level: 4, color: '#f97316' },
  { min: 0.18, max: 0.35, label: 'Very Hard', level: 5, color: '#ef4444' },
  { min: 0.00, max: 0.18, label: 'Expert', level: 6, color: '#dc2626' },
];

const getDifficultyFromRate = (rate: number) => {
  for (const band of DIFFICULTY_BANDS) {
    if (rate >= band.min && rate < band.max) return band;
  }
  return DIFFICULTY_BANDS[5];
};

// Calculate confidence based on sample size and proportion
// Using Wilson score interval approximation
const calculateConfidence = (n: number, p: number): { confidence: number; marginOfError: number; lowerBound: number; upperBound: number } => {
  if (n === 0) return { confidence: 0, marginOfError: 0.5, lowerBound: 0, upperBound: 1 };

  // Z-score for 95% confidence
  const z = 1.96;

  // Margin of error using normal approximation
  const marginOfError = z * Math.sqrt((p * (1 - p)) / n);

  // Confidence increases with sample size (asymptotic to 99%)
  // Using a logarithmic scale: confidence = 1 - 1/(1 + log(n+1)/k)
  const confidence = Math.min(0.99, 1 - 1 / (1 + Math.log10(n + 1) / 1.5));

  const lowerBound = Math.max(0, p - marginOfError);
  const upperBound = Math.min(1, p + marginOfError);

  return { confidence, marginOfError, lowerBound, upperBound };
};

const INITIAL_QUESTIONS: QuestionData[] = [
  {
    id: 'q1',
    text: 'What is 5 + 3?',
    initialDifficulty: 3,
    currentDifficulty: 3,
    totalAttempts: 0,
    correctAttempts: 0,
    correctRate: 0.5,
  },
  {
    id: 'q2',
    text: 'Solve: 2x + 4 = 12',
    initialDifficulty: 4,
    currentDifficulty: 4,
    totalAttempts: 0,
    correctAttempts: 0,
    correctRate: 0.5,
  },
  {
    id: 'q3',
    text: 'Find the derivative of x³ + 2x',
    initialDifficulty: 4,
    currentDifficulty: 4,
    totalAttempts: 0,
    correctAttempts: 0,
    correctRate: 0.5,
  },
  {
    id: 'q4',
    text: 'Prove: lim(x→0) sin(x)/x = 1',
    initialDifficulty: 5,
    currentDifficulty: 5,
    totalAttempts: 0,
    correctAttempts: 0,
    correctRate: 0.5,
  },
];

// Target correct rates - what the "true" difficulty converges to
const TRUE_CORRECT_RATES: Record<string, number> = {
  q1: 0.94, // Very Easy
  q2: 0.62, // Medium
  q3: 0.28, // Very Hard
  q4: 0.12, // Expert
};

const TeacherSlide08DifficultyCalibration: React.FC = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [questions, setQuestions] = useState<QuestionData[]>(INITIAL_QUESTIONS);
  const [totalResponses, setTotalResponses] = useState(0);
  const simInterval = useRef<NodeJS.Timeout | null>(null);

  const simulateResponse = (questionId: string): boolean => {
    const trueRate = TRUE_CORRECT_RATES[questionId];
    const variance = (Math.random() - 0.5) * 0.08;
    return Math.random() < (trueRate + variance);
  };

  useEffect(() => {
    if (!isPlaying) {
      if (simInterval.current) {
        clearInterval(simInterval.current);
        simInterval.current = null;
      }
      return;
    }

    simInterval.current = setInterval(() => {
      const questionIds = Object.keys(TRUE_CORRECT_RATES);
      const randomId = questionIds[Math.floor(Math.random() * questionIds.length)];
      const isCorrect = simulateResponse(randomId);

      setQuestions(prev => prev.map(q => {
        if (q.id !== randomId) return q;

        const newTotal = q.totalAttempts + 1;
        const newCorrect = q.correctAttempts + (isCorrect ? 1 : 0);
        const newRate = newCorrect / newTotal;
        const newDifficulty = getDifficultyFromRate(newRate);

        return {
          ...q,
          totalAttempts: newTotal,
          correctAttempts: newCorrect,
          correctRate: newRate,
          currentDifficulty: newDifficulty.level,
        };
      }));

      setTotalResponses(prev => prev + 1);
    }, 350);

    return () => {
      if (simInterval.current) {
        clearInterval(simInterval.current);
      }
    };
  }, [isPlaying]);

  const reset = () => {
    setIsPlaying(false);
    setQuestions(INITIAL_QUESTIONS);
    setTotalResponses(0);
  };

  // Generate bell curve points
  const bellCurve: { x: number; y: number }[] = [];
  for (let x = 0; x <= 100; x += 2) {
    const normalized = (x - 50) / 15;
    const y = Math.exp(-0.5 * normalized * normalized);
    bellCurve.push({ x, y: y * 100 });
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 pt-3 pb-2">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">Dynamic Difficulty Calibration</h2>
            <p className="text-xs text-[#94a3b8]">Question difficulty determined by aggregate student performance with statistical confidence</p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#0ea5e9]/15 border border-[#0ea5e9]/30">
            <UserGroupIcon className="w-4 h-4 text-[#0ea5e9]" />
            <span className="text-sm font-bold text-[#0ea5e9]">{totalResponses.toLocaleString()} responses</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex gap-4 px-6 pb-2 min-h-0">
        {/* Left: Question Cards */}
        <div className="flex-1 flex flex-col gap-2">
          <div className="text-sm font-semibold text-[#64748b] uppercase tracking-wider mb-1">
            Live Question Calibration
          </div>

          {questions.map((q) => {
            const band = getDifficultyFromRate(q.correctRate);
            const { confidence, marginOfError, lowerBound, upperBound } = calculateConfidence(q.totalAttempts, q.correctRate);
            const confidencePercent = (confidence * 100).toFixed(0);

            return (
              <div
                key={q.id}
                className="p-3 rounded-xl"
                style={{
                  background: 'rgba(15, 23, 42, 0.9)',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="text-white font-medium text-sm">{q.text}</p>
                  </div>
                  <div
                    className="px-2 py-0.5 rounded text-xs font-bold"
                    style={{ background: `${band.color}20`, color: band.color, border: `1px solid ${band.color}50` }}
                  >
                    {band.label}
                  </div>
                </div>

                {/* Correct rate bar with confidence interval */}
                <div className="mb-3">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-[#64748b]">Correct Rate</span>
                    <span className="font-bold text-white">
                      {(q.correctRate * 100).toFixed(1)}%
                      {q.totalAttempts > 0 && (
                        <span className="text-[#64748b] font-normal ml-1">
                          (±{(marginOfError * 100).toFixed(1)}%)
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="h-2 bg-[#1e293b] rounded-full overflow-hidden relative">
                    {/* Confidence interval range (lighter background) */}
                    {q.totalAttempts > 0 && (
                      <div
                        className="absolute h-full rounded-full opacity-30"
                        style={{
                          background: band.color,
                          left: `${lowerBound * 100}%`,
                          width: `${(upperBound - lowerBound) * 100}%`,
                        }}
                      />
                    )}
                    {/* Actual rate bar */}
                    <motion.div
                      className="h-full rounded-full relative z-10"
                      style={{ background: band.color }}
                      animate={{ width: `${q.correctRate * 100}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>

                {/* Stats row */}
                <div className="flex items-center gap-3 text-xs">
                  {/* Sample size */}
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-[#1e293b]">
                    <span className="text-[#64748b]">n =</span>
                    <span className="font-bold text-white">{q.totalAttempts.toLocaleString()}</span>
                  </div>

                  {/* Confidence */}
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-[#1e293b]">
                    <span className="text-[#64748b]">Confidence:</span>
                    <span
                      className="font-bold"
                      style={{
                        color: confidence > 0.8 ? '#10b981' : confidence > 0.5 ? '#f59e0b' : '#ef4444'
                      }}
                    >
                      {q.totalAttempts === 0 ? '—' : `${confidencePercent}%`}
                    </span>
                  </div>

                  {/* Spacer */}
                  <div className="flex-1" />

                  {/* Initial vs Calibrated */}
                  <div className="flex items-center gap-2">
                    <div className="text-center px-2 py-1 rounded bg-[#1e293b]">
                      <span className="text-[#64748b]">Initial: </span>
                      <span className="font-bold text-[#64748b]">{q.initialDifficulty}</span>
                    </div>
                    <span className="text-[#64748b]">→</span>
                    <div
                      className="text-center px-2 py-1 rounded"
                      style={{
                        background: `${band.color}15`,
                        border: `1px solid ${band.color}30`
                      }}
                    >
                      <span className="text-[#64748b]">Now: </span>
                      <span className="font-bold" style={{ color: band.color }}>{q.currentDifficulty}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Explanation */}
          <div
            className="mt-auto p-3 rounded-xl"
            style={{ background: 'rgba(14, 165, 233, 0.1)', border: '1px solid rgba(14, 165, 233, 0.3)' }}
          >
            <p className="text-xs text-[#94a3b8]">
              <span className="text-[#0ea5e9] font-semibold">Confidence-based calibration:</span> Difficulty adjustments
              are weighted by statistical confidence. Small sample sizes have wide margins of error; as more students
              answer, confidence increases and calibration stabilizes.
            </p>
          </div>
        </div>

        {/* Right: Chart & Bands */}
        <div className="w-72 flex flex-col gap-3">
          {/* Chart */}
          <div className="rounded-xl p-4 flex-1" style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="flex items-center gap-2 mb-3">
              <ChartBarIcon className="w-4 h-4 text-[#0ea5e9]" />
              <span className="text-sm font-semibold text-white">Performance Distribution</span>
            </div>

            {/* Chart with HTML markers */}
            <div className="relative h-28 mb-2">
              <svg viewBox="0 0 100 60" className="w-full h-full" preserveAspectRatio="none">
                {/* Difficulty band backgrounds */}
                {DIFFICULTY_BANDS.map((band, i) => (
                  <rect
                    key={i}
                    x={band.min * 100}
                    y={0}
                    width={(band.max - band.min) * 100}
                    height={60}
                    fill={band.color}
                    opacity={0.15}
                  />
                ))}

                {/* Bell curve */}
                <path
                  d={`M ${bellCurve.map(p => `${p.x},${60 - p.y * 0.55}`).join(' L ')}`}
                  fill="none"
                  stroke="#64748b"
                  strokeWidth="1.5"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>

              {/* HTML markers for questions */}
              {questions.map((q) => {
                const xPercent = q.correctRate * 100;
                const normalized = (xPercent - 50) / 15;
                // Match the SVG curve: y = 60 - gaussianValue * 55
                // Convert to bottom percentage: (60 - svgY) / 60 * 100
                const gaussianValue = Math.exp(-0.5 * normalized * normalized);
                const yPercent = (gaussianValue * 55 / 60) * 100;
                const band = getDifficultyFromRate(q.correctRate);
                const { confidence } = calculateConfidence(q.totalAttempts, q.correctRate);

                return (
                  <motion.div
                    key={q.id}
                    className="absolute rounded-full border-2 border-white"
                    style={{
                      width: 10,
                      height: 10,
                      background: band.color,
                      transform: 'translate(-50%, 50%)',
                      opacity: 0.4 + confidence * 0.6,
                    }}
                    animate={{
                      left: `${xPercent}%`,
                      bottom: `${yPercent}%`,
                    }}
                    transition={{ duration: 0.5 }}
                  />
                );
              })}
            </div>

            {/* X-axis labels */}
            <div className="flex justify-between text-[9px] text-[#64748b] mb-3">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
            <div className="text-center text-[10px] text-[#64748b] mb-3">Correct Rate →</div>

            {/* Difficulty bands legend */}
            <div className="space-y-1">
              {DIFFICULTY_BANDS.map((band) => (
                <div key={band.label} className="flex items-center gap-2 text-[10px]">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ background: band.color }} />
                  <span className="text-[#94a3b8] flex-1">{band.label}</span>
                  <span className="text-[#64748b] font-mono">{(band.min * 100).toFixed(0)}-{(band.max * 100).toFixed(0)}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Confidence Scale */}
          <div
            className="p-3 rounded-xl"
            style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <h4 className="text-xs font-semibold text-white mb-2">Confidence Thresholds</h4>
            <div className="space-y-1.5 text-[10px]">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#ef4444]" />
                <span className="text-[#94a3b8]">&lt;50%</span>
                <span className="text-[#64748b] flex-1 text-right">Low confidence (n &lt; 10)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#f59e0b]" />
                <span className="text-[#94a3b8]">50-80%</span>
                <span className="text-[#64748b] flex-1 text-right">Moderate (n = 10-50)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#10b981]" />
                <span className="text-[#94a3b8]">&gt;80%</span>
                <span className="text-[#64748b] flex-1 text-right">High confidence (n &gt; 50)</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="rounded-xl p-2 grid grid-cols-2 gap-2" style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="text-center">
              <div className="text-lg font-bold text-white">{questions.filter(q => q.currentDifficulty !== q.initialDifficulty).length}</div>
              <div className="text-[9px] text-[#64748b]">Recalibrated</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-[#10b981]">
                {questions.reduce((sum, q) => sum + q.totalAttempts, 0).toLocaleString()}
              </div>
              <div className="text-[9px] text-[#64748b]">Total Attempts</div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="px-6 py-2 flex items-center justify-center gap-4" style={{ background: 'rgba(15,23,42,0.9)', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          className="flex items-center gap-1.5 px-5 py-2 rounded-lg font-medium text-sm"
          style={{
            background: isPlaying ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)',
            border: `2px solid ${isPlaying ? 'rgba(239,68,68,0.5)' : 'rgba(16,185,129,0.5)'}`,
            color: isPlaying ? '#ef4444' : '#10b981',
          }}
        >
          {isPlaying ? <><PauseIcon className="w-4 h-4" /> Pause</> : <><PlayIcon className="w-4 h-4" /> {totalResponses > 0 ? 'Resume' : 'Start Simulation'}</>}
        </button>
        <button onClick={reset} className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-[#94a3b8] hover:text-white hover:bg-[#334155]">
          <ArrowPathIcon className="w-4 h-4" /> Reset
        </button>
        <div className="text-sm text-[#64748b]">Watch confidence increase as sample size grows</div>
      </div>
    </div>
  );
};

export default TeacherSlide08DifficultyCalibration;
